import { participantsOf } from "../entities/trip.js";
export const emptyCompletion = () => ({ byParticipant: {} });
// --- internal record helpers (immutable) ---
const doneEntries = (map) => Object.entries(map.byParticipant).filter(([, e]) => e.done);
// Which map a Completable operation acts on — `completions` (packed) or `staging` (staged).
// Defaults to `completions` everywhere so existing (pre-T27) call sites are unaffected.
const mapOf = (item, field) => field === "completions" ? item.completions : item.staging ?? emptyCompletion();
const withMap = (item, field, map) => field === "completions" ? { ...item, completions: map } : { ...item, staging: map };
export const anyDone = (item, field = "completions") => doneEntries(mapOf(item, field)).length > 0;
const setEntry = (map, pid, entry) => ({
    byParticipant: { ...map.byParticipant, [pid]: entry },
});
const clearEntry = (map, pid) => {
    const { [pid]: _removed, ...rest } = map.byParticipant;
    return { byParticipant: rest };
};
// How to "uncheck" a *shared* item. A shared item reads as done if anyone is marked, so
// the single checkbox is turned off by clearing every recorded check. (Considered and
// rejected: clearing only the actor's own entry — for a shared "anyone brings it" item the
// single box should mean a single decision, not a per-person one.)
export const resolveSharedUncheck = (_completions) => emptyCompletion();
export const requiredCount = (item, participants) => {
    switch (item.assignment.kind) {
        case "shared": return 1;
        case "everyone": return participants.length;
        case "specific": return item.assignment.allocations.length;
    }
};
// How many checks count toward the current view: shared collapses to 0/1; everyone counts
// every recorded check; specific counts only its allocatees (so checks recorded under a
// previous, broader assignment don't inflate the count). `field` picks completions
// (packed) or staging (staged) — same fan-out logic for both (T27).
export const checkedCount = (item, field = "completions") => {
    const map = mapOf(item, field);
    switch (item.assignment.kind) {
        case "shared": return doneEntries(map).length > 0 ? 1 : 0;
        case "everyone": return doneEntries(map).length;
        case "specific": {
            const allowed = new Set(item.assignment.allocations.map((a) => a.participantId));
            return doneEntries(map).filter(([pid]) => allowed.has(pid)).length;
        }
    }
};
// `field` (issue #188) asks the same question of the staging map instead: a template
// row is "done" when every relevant traveler is staged for it.
export const isItemComplete = (item, participants, field = "completions") => {
    const req = requiredCount(item, participants);
    return req > 0 && checkedCount(item, field) === req;
};
export const computeProgress = (items, participants, field = "completions") => {
    let checked = 0;
    let required = 0;
    for (const item of items) {
        checked += checkedCount(item, field);
        required += requiredCount(item, participants);
    }
    const ratio = required === 0 ? 0 : checked / required;
    return { checked, required, ratio };
};
// targetIsUser must be true when target is a real user account (not a managed profile).
// Actors cannot proxy-toggle another user's checkbox — unless the trip-level
// `allowProxyCompletion` setting is on (T23 §2). They can ALWAYS toggle a managed
// profile's. Generic so it returns the same entity type it was given (item or to-do).
// `field` (T27) picks which map — completions (packed) or staging (staged) — the toggle
// applies to; defaults to completions so pre-T27 call sites are unaffected.
export const applyToggle = (item, actor, target, targetIsUser, opts) => {
    if (targetIsUser && target !== actor && !(opts?.allowProxyCompletion ?? false)) {
        return { ok: false, reason: "cannot-proxy-user" };
    }
    const field = opts?.field ?? "completions";
    const map = mapOf(item, field);
    const now = new Date();
    // Shared: one logical checkbox. Checking records the actor; unchecking is delegated to
    // resolveSharedUncheck (the actor always "owns" the click, so no proxy concern).
    if (item.assignment.kind === "shared") {
        const next = doneEntries(map).length > 0
            ? resolveSharedUncheck(map)
            : setEntry(map, actor, { done: true, checkedBy: actor, at: now });
        return { ok: true, item: withMap(item, field, next) };
    }
    // everyone / specific: toggle the target participant's own entry.
    const alreadyDone = map.byParticipant[target]?.done ?? false;
    const next = alreadyDone
        ? clearEntry(map, target)
        : setEntry(map, target, { done: true, checkedBy: actor, at: now });
    return { ok: true, item: withMap(item, field, next) };
};
// Per-participant done-state changes between two records — the LWW path writes needed to
// persist a toggle. One change for an ordinary toggle; several when a shared uncheck clears
// multiple preserved entries at once.
export const completionDelta = (before, after) => {
    const pids = new Set([
        ...Object.keys(before.byParticipant),
        ...Object.keys(after.byParticipant),
    ]);
    const out = [];
    for (const pid of pids) {
        const b = before.byParticipant[pid]?.done ?? false;
        const a = after.byParticipant[pid]?.done ?? false;
        if (b !== a)
            out.push([pid, a]);
    }
    return out;
};
// Which participants a row is completed against: a 'specific'/individuals row only its
// allocatees; 'everyone' (and 'shared', where the single checkbox stands for the whole
// group) all trip participants. Used to render one avatar chip per relevant participant
// and to size the checkbox-visibility/self-toggle predicate below.
//
// Ordered by `participantsOf(list)` (not by `allocations` array order) even in the
// 'specific' case, so a row's avatar chips always land in the same order regardless of
// the order participants were toggled on — and so the ItemComposer's ASSIGN picker
// (itself ordered by `participantsOf`) shows assignees in the same order as the row.
export const relevantParticipants = (item, list) => {
    if (item.assignment.kind !== "specific")
        return participantsOf(list);
    const allocated = new Set(item.assignment.allocations.map((a) => a.participantId));
    return participantsOf(list).filter((pid) => allocated.has(pid));
};
// Can `userId` check this row off themselves? True for a 'shared' item (anyone may toggle
// the single shared box) or when the user is a relevant participant on an 'everyone'/
// 'specific' item (they'd be toggling their own entry, never someone else's — see the
// proxy rule in `applyToggle`). Drives whether a row shows a checkbox at all (Doc 06 §4 /
// gap-analysis T-E): hidden for a 'specific' item assigned only to other people.
export const canSelfComplete = (item, list, userId) => item.assignment.kind === "shared" || relevantParticipants(item, list).includes(userId);
// The actor's own done-state for a field, collapsing "shared" the same way applyToggle
// reads it (a single record, not per-participant).
const ownDone = (item, actor, field) => item.assignment.kind === "shared"
    ? anyDone(item, field)
    : (mapOf(item, field).byParticipant[actor]?.done ?? false);
// Category-wide "More" menu bulk actions: force-set (not cycle) the actor's own
// completion to `done` on every eligible row in the category — so a category left in
// mixed states always ends up fully checked or fully unchecked in one call, unlike
// `advanceCategoryStaging`'s one-step-per-item cycle. Generic over anything Completable
// with a `categoryId`, so the same function drives both packing items and to-dos.
export const setCategoryCompletion = (items, categoryId, list, actor, done, opts) => items.map((item) => {
    if (item.categoryId !== categoryId || !canSelfComplete(item, list, actor))
        return item;
    let next = item;
    if (ownDone(next, actor, "completions") !== done) {
        const r = applyToggle(next, actor, actor, true, { ...opts, field: "completions" });
        if (r.ok)
            next = r.item;
    }
    // Unchecking also clears staging (T27): otherwise a fully-unchecked item could still
    // read as "staged". Checking doesn't need to touch staging — packingStateOf reads
    // "packed" off completions alone, regardless of the staging map.
    if (!done && next.staging !== undefined && ownDone(next, actor, "staging")) {
        const r = applyToggle(next, actor, actor, true, { ...opts, field: "staging" });
        if (r.ok)
            next = r.item;
    }
    return next;
});
// One row's item-level "check/uncheck all" (issue #203's expanded-mode control) — the other
// bulk axis from `setCategoryCompletion` (many items, one actor): here it's one item, every
// relevant participant. Each participant's own entry is only touched via `applyToggle`, so
// the existing proxy rule applies per-participant: when `allowProxyCompletion` is off, only
// the actor's own pill (and any managed profile's) actually moves — other users' entries are
// silently left as-is, which *is* the "check-all only affects yourself" behavior.
export const setAllParticipantsCompletion = (item, list, actor, done, opts) => {
    const field = opts?.field ?? "completions";
    const isDone = (it, pid, f) => {
        const map = f === "completions" ? it.completions : it.staging ?? emptyCompletion();
        return map.byParticipant[pid]?.done ?? false;
    };
    // Shared: one logical checkbox for the whole row — applyToggle already flips it as the
    // actor, so there's nothing to loop over participants for.
    if (item.assignment.kind === "shared") {
        if (anyDone(item, field) === done)
            return item;
        const r = applyToggle(item, actor, actor, true, { ...opts, field });
        return r.ok ? r.item : item;
    }
    let next = item;
    for (const pid of relevantParticipants(item, list)) {
        const member = list.members[pid];
        if (member === undefined || isDone(next, pid, field) === done)
            continue;
        const r = applyToggle(next, actor, pid, member.kind === "user", { ...opts, field });
        if (r.ok)
            next = r.item;
    }
    // Unchecking also clears staging (mirrors setCategoryCompletion, per participant here).
    if (!done && field === "completions" && next.staging !== undefined) {
        for (const pid of relevantParticipants(next, list)) {
            const member = list.members[pid];
            if (member === undefined || !isDone(next, pid, "staging"))
                continue;
            const r = applyToggle(next, actor, pid, member.kind === "user", { ...opts, field: "staging" });
            if (r.ok)
                next = r.item;
        }
    }
    return next;
};
export const clearCompletions = (items, scope) => items.map((item) => scope === "all" || item.assignment.kind === "shared"
    ? { ...item, completions: emptyCompletion() }
    : item);
//# sourceMappingURL=completion.js.map